import { Button, Card, CardContent } from "@exhousing/design-system";
import {
  CalendarPlus,
  Check,
  Compass,
  Crown,
  FilePlus2,
  FileSignature,
  Loader2,
  ThumbsDown,
  ThumbsUp,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { RequestModal } from "@/components/application/DocumentsCard";
import { SelectModal } from "@/components/application/SelectModal";
import {
  useCreateContractFromApplication,
  useDecideApplication,
  useInviteApplication,
  useRecordAttendance,
  useWithdrawOffer,
} from "@/hooks/api/useApplications";
import type { components } from "@/types/api";

type Detail = components["schemas"]["ApplicationDetail"];

function formatSlot(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The dossier cockpit: one card that always says where the candidate stands and what
 * the agency's next move is — no hunting across pages (journeys 4–8 from one place).
 */
export function NextStepCard({
  app,
  landlordView = false,
}: {
  app: Detail;
  landlordView?: boolean;
}) {
  const navigate = useNavigate();
  const invite = useInviteApplication();
  const attendance = useRecordAttendance();
  const decide = useDecideApplication();
  const createContract = useCreateContractFromApplication();
  const withdraw = useWithdrawOffer();
  const [showSelect, setShowSelect] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const withdrawButton = (
    <Button
      size="sm"
      variant="outline"
      disabled={withdraw.isPending}
      onClick={() => {
        if (!confirmWithdraw) {
          setConfirmWithdraw(true);
          return;
        }
        withdraw.mutate(app.id, {
          onSuccess: () => {
            toast.success(
              "Offer withdrawn — the candidate is back in the approved pool. Inform them yourself; no automatic mail was sent.",
            );
            setConfirmWithdraw(false);
          },
          onError,
        });
      }}
    >
      {withdraw.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <UserX className="size-4" />
      )}
      {confirmWithdraw ? "Confirm withdraw" : "Withdraw offer"}
    </Button>
  );

  const fullName = `${app.first_name} ${app.last_name}`.trim();
  const onError = (err: Error) => toast.error(err.message);

  let body: React.ReactNode = null;

  if (landlordView) {
    // The landlord's portal: read-only narration, with Select as the only action.
    switch (app.status) {
      case "approved":
      case "reserve":
        body = (
          <Step text="This candidate is approved. Selecting sends them a 14-day offer; other finalists are handled automatically with the recorded reason.">
            <Button size="sm" onClick={() => setShowSelect(true)}>
              <Crown className="size-4" /> Select candidate
            </Button>
          </Step>
        );
        break;
      case "under_option":
        body = (
          <Step
            text={
              app.offer_deadline
                ? `Offer sent — the candidate has until ${formatSlot(app.offer_deadline)} to respond.`
                : "Offer sent — waiting for the candidate's decision."
            }
          />
        );
        break;
      case "contract_phase":
        body = (
          <Step text="The candidate accepted the offer. Your agency prepares the contract; you sign via the e-mail you'll receive." />
        );
        break;
      case "tenant":
        body = <Step text="Done — this candidate is your tenant." />;
        break;
      default:
        body = (
          <Step text="Your agency is guiding this candidate through the process. You decide once candidates reach the final selection." />
        );
    }
    return (
      <Card className="border-primary/30 bg-primary/[0.03]">
        <CardContent className="flex items-start gap-3 pt-5">
          <Compass className="mt-0.5 size-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Next step</p>
            {body}
          </div>
        </CardContent>
        {showSelect && (
          <SelectModal
            applicationId={app.id}
            candidateName={fullName}
            onClose={() => setShowSelect(false)}
          />
        )}
      </Card>
    );
  }

  switch (app.status) {
    case "new":
    case "incomplete":
      body =
        app.kind === "question" ? (
          <Step text="The candidate asked a question. Answer it by e-mail; close the dossier once handled, or wait — they can upgrade it to a full application via their link." />
        ) : (
          <Step text="Waiting for the candidate to finish their application. Automatic reminders go out after 48 hours and 7 days; it expires after 30 days." />
        );
      break;
    case "preselection":
      body = (
        <Step text="The application is complete and scored. Invite the candidate to a viewing (needs an open future slot), or use the bulk invite on the property's candidate list.">
          <Button
            size="sm"
            disabled={invite.isPending}
            onClick={() =>
              invite.mutate(app.id, {
                onSuccess: () => toast.success("Viewing invitation sent"),
                onError,
              })
            }
          >
            {invite.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <CalendarPlus className="size-4" />
            )}
            Invite to viewing
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/viewings")}>
            Manage slots
          </Button>
        </Step>
      );
      break;
    case "invited":
      body = (
        <Step text="Invitation sent — waiting for the candidate to pick a time slot through their personal link." />
      );
      break;
    case "viewing_planned":
      body = (
        <Step
          text={
            app.viewing_slot_at
              ? `Viewing booked for ${formatSlot(app.viewing_slot_at)}. Register the outcome afterwards — attended moves the candidate on, a no-show closes the dossier.`
              : "Viewing booked. Register the outcome afterwards."
          }
        >
          <Button
            size="sm"
            disabled={attendance.isPending}
            onClick={() =>
              attendance.mutate(
                { applicationId: app.id, attended: true },
                { onSuccess: () => toast.success("Marked attended"), onError },
              )
            }
          >
            <Check className="size-4" /> Attended
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={attendance.isPending}
            onClick={() =>
              attendance.mutate(
                { applicationId: app.id, attended: false },
                { onSuccess: () => toast.success("Marked as no-show"), onError },
              )
            }
          >
            <UserX className="size-4" /> No-show
          </Button>
        </Step>
      );
      break;
    case "viewing_done":
      body = (
        <Step text="The candidate attended and got the interest follow-up by mail. You don't have to wait — requesting documents moves the dossier on right away.">
          <Button size="sm" onClick={() => setShowRequest(true)}>
            <FilePlus2 className="size-4" /> Request documents
          </Button>
        </Step>
      );
      break;
    case "documents_requested":
      body = (
        <Step text="Waiting for uploads — review each document below as it comes in. A reminder goes out automatically after 3 days. Accepting all required documents moves the dossier on." />
      );
      break;
    case "documents_received":
    case "screening":
      body = (
        <Step text="All required documents are accepted. Take the decision: approved candidates enter the final selection.">
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { applicationId: app.id, approve: true },
                { onSuccess: () => toast.success("Candidate approved"), onError },
              )
            }
          >
            <ThumbsUp className="size-4" /> Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() =>
              decide.mutate(
                { applicationId: app.id, approve: false },
                { onSuccess: () => toast.success("Candidate rejected"), onError },
              )
            }
          >
            <ThumbsDown className="size-4" /> Reject
          </Button>
        </Step>
      );
      break;
    case "approved":
    case "reserve":
      body = (
        <Step text="Approved and ready for the final selection. Selecting sends a 14-day offer; other finalists move to reserve or are rejected automatically with the recorded reason.">
          <Button size="sm" onClick={() => setShowSelect(true)}>
            <Crown className="size-4" /> Select candidate
          </Button>
        </Step>
      );
      break;
    case "under_option":
      body = (
        <Step
          text={
            app.offer_deadline
              ? `Offer sent — the candidate has until ${formatSlot(app.offer_deadline)} to respond. A lapse automatically re-opens the property.`
              : "Offer sent — waiting for the candidate's decision."
          }
        >
          {withdrawButton}
        </Step>
      );
      break;
    case "contract_phase":
      body = app.contract_id ? (
        <Step text="The contract has been drafted from this dossier. Finish and activate it there — activation makes the candidate the tenant.">
          <Button size="sm" onClick={() => navigate(`/contracts/${app.contract_id}`)}>
            <FileSignature className="size-4" /> Open contract
          </Button>
          {withdrawButton}
        </Step>
      ) : (
        <Step text="The candidate accepted the offer. Draft the contract straight from this dossier — tenant details and start date pre-fill automatically.">
          <Button
            size="sm"
            disabled={createContract.isPending}
            onClick={() =>
              createContract.mutate(
                { applicationId: app.id, propertyId: app.property_id },
                {
                  onSuccess: (contract) => {
                    toast.success("Draft contract created");
                    navigate(`/contracts/${contract.id}`);
                  },
                  onError,
                },
              )
            }
          >
            {createContract.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileSignature className="size-4" />
            )}
            Draft contract
          </Button>
          {withdrawButton}
        </Step>
      );
      break;
    case "tenant":
      body = (
        <Step text="Done — this candidate is the tenant. The dossier stays as the audit trail of the selection.">
          <Link
            to={app.contract_id ? `/contracts/${app.contract_id}` : "/contracts"}
            className="text-sm font-medium text-primary hover:underline"
          >
            Open contract
          </Link>
        </Step>
      );
      break;
    default:
      return null; // terminal cancelled/rejected: the status banner says enough
  }

  return (
    <Card className="border-primary/30 bg-primary/[0.03]">
      <CardContent className="flex items-start gap-3 pt-5">
        <Compass className="mt-0.5 size-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Next step</p>
          {body}
        </div>
      </CardContent>

      {showSelect && (
        <SelectModal
          applicationId={app.id}
          candidateName={fullName}
          onClose={() => setShowSelect(false)}
        />
      )}
      {showRequest && (
        <RequestModal
          applicationId={app.id}
          incomeType={app.income_type ?? null}
          onClose={() => setShowRequest(false)}
        />
      )}
    </Card>
  );
}

function Step({ text, children }: { text: string; children?: React.ReactNode }) {
  return (
    <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
      <p className="min-w-0 flex-1 text-sm text-muted-foreground">{text}</p>
      {children && <div className="flex shrink-0 gap-2">{children}</div>}
    </div>
  );
}
